import { SignInIdentifier } from '@logto/schemas';
import type { TFunction } from 'i18next';
import { HTTPError } from 'ky';

import { type ErrorHandlers } from '@/hooks/use-error-handler';

export const RESEND_SECONDS = 59;

export const getTimerExpiry = () => {
  const now = new Date();
  now.setSeconds(now.getSeconds() + RESEND_SECONDS);

  return now;
};

/** 从 ky HTTPError 的响应体里取 Logto 错误码 */
export const getErrorCode = async (error: unknown) => {
  if (error instanceof HTTPError) {
    const body: unknown = await error.response.clone().json();

    if (body && typeof body === 'object' && 'code' in body && typeof body.code === 'string') {
      return body.code;
    }
  }
};

/** 从错误 data 里取 retryAfter 秒数;形状不对返回 undefined,由调用方走默认倒计时 */
export const getRetryAfter = (data: unknown) => {
  if (data && typeof data === 'object' && 'retryAfter' in data) {
    const retryAfter = Number(data.retryAfter);

    if (Number.isFinite(retryAfter) && retryAfter > 0) {
      return retryAfter;
    }
  }
};

export const getAvailableResetMethods = (enabledMethods: ReadonlySet<SignInIdentifier>) =>
  (
    [
      ['phone', SignInIdentifier.Phone],
      ['email', SignInIdentifier.Email],
    ] as const
  )
    .filter(([, identifier]) => enabledMethods.has(identifier))
    .map(([method]) => method);

/** 「手机号验证重置 | 邮件验证码重置」页签 */
export const buildMethodTabs = (methods: ReadonlyArray<'phone' | 'email'>, translate: TFunction) =>
  methods.map((value) => ({
    key: value,
    label:
      value === 'phone'
        ? translate('eva_domestic.phone_reset')
        : translate('eva_domestic.email_reset'),
  }));

/** 字段变更时顺手清掉对应错误态 */
export const bindField =
  <T>(setter: (value: T) => void, clearError: () => void) =>
  (value: T) => {
    setter(value);
    clearError();
  };

type PasswordLiveSettersDeps = {
  t: TFunction;
  password: string;
  confirmPassword: string;
  setPassword: (value: string) => void;
  setPasswordError: (value: string) => void;
  setConfirmPassword: (value: string) => void;
  setConfirmError: (value: string) => void;
};

/**
 * 两次输入一致性实时校验(与个人中心「设置密码」弹窗同规):
 * 确认框有输入且不一致就提示;一致或清空则收起。
 */
export const buildPasswordLiveSetters = ({
  t,
  password,
  confirmPassword,
  setPassword,
  setPasswordError,
  setConfirmPassword,
  setConfirmError,
}: PasswordLiveSettersDeps) => ({
  setPasswordLive: (value: string) => {
    setPassword(value);
    setPasswordError('');
    if (confirmPassword) {
      setConfirmError(value === confirmPassword ? '' : t('eva_domestic.password_mismatch'));
    }
  },
  setConfirmPasswordLive: (value: string) => {
    setConfirmPassword(value);
    setConfirmError(value && value !== password ? t('eva_domestic.password_mismatch') : '');
  },
});

type SendCodeErrorHandlersDeps = {
  t: TFunction;
  invalidIdentifierMessage: string;
  setIdentifierError: (value: string) => void;
  setCodeError: (value: string) => void;
  /** 按服务端 retryAfter 秒数重启冷却倒计时 */
  restartCooldown: (seconds: number) => void;
};

/**
 * 发码失败的错误处理:格式错/账号未注册 → 标识符字段红字;
 * 限流 → 按服务端 retryAfter 校准冷却并提示;其余 → 验证码字段红字。
 */
export const buildSendCodeErrorHandlers = ({
  t,
  invalidIdentifierMessage,
  setIdentifierError,
  setCodeError,
  restartCooldown,
}: SendCodeErrorHandlersDeps): ErrorHandlers => ({
  'guard.invalid_input': () => {
    setIdentifierError(invalidIdentifierMessage);
  },
  'user.user_not_exist': () => {
    setIdentifierError(t('eva_domestic.account_not_registered'));
  },
  // 服务端重发冷却命中:按 retryAfter 重启倒计时(服务端权威)
  'verification_code.rate_limited': (error) => {
    const retryAfter = getRetryAfter(error.data);
    if (retryAfter) {
      restartCooldown(retryAfter);
    }
    setCodeError(error.message);
  },
  global: (error) => {
    setCodeError(error.message);
  },
});
